import React from "react";
import { useDispatch } from "react-redux";
import { SetIPhoneModeAction } from "../../stores/actions/userAction";
import { MapPin, Briefcase, X } from "lucide-react";

interface IPhoneModeSelectionModalProps {
  open: boolean;
  onClose?: () => void;
  onSelectMode?: (mode: "attendance" | "other_operations") => void;
}

const IPhoneModeSelectionModal: React.FC<IPhoneModeSelectionModalProps> = ({ open, onClose, onSelectMode }) => {
  const dispatch = useDispatch();

  if (!open) return null;

  const handleSelectMode = (mode: "attendance" | "other_operations") => {
    dispatch(SetIPhoneModeAction(mode));
    onSelectMode?.(mode);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-[350px] sm:max-w-sm max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl p-5 sm:p-6 flex flex-col items-center text-center border border-gray-100/80">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="w-12 h-12 rounded-2xl bg-[#FFF3EB] flex items-center justify-center mb-3 text-[#FF6B1A] shadow-xs shrink-0">
          <MapPin className="w-6 h-6" />
        </div>

        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
          Select Application Mode
        </h2>
        <p className="text-[11px] sm:text-xs text-gray-500 mb-5 max-w-[260px]">
          Choose how you want to experience the HRMS portal on your iPhone
        </p>

        <div className="w-full space-y-3">
          <button
            onClick={() => handleSelectMode("attendance")}
            className="w-full group flex items-start p-3.5 sm:p-4 rounded-2xl border-2 border-[#FF6B1A] bg-[#FFF3EB]/40 hover:bg-[#FFF3EB] transition-all text-left relative overflow-hidden shadow-sm active:scale-[0.98]"
          >
            <div className="p-2.5 rounded-xl bg-[#FF6B1A] text-white mr-3 shrink-0 shadow-sm">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xs sm:text-sm font-bold text-gray-900 group-hover:text-[#FF6B1A] transition-colors">
                Attendance Mode
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 leading-snug">
                Quick Clock In/Out, selfie capture & attendance calendar
              </p>
            </div>
          </button>

          <button
            onClick={() => handleSelectMode("other_operations")}
            className="w-full group flex items-start p-3.5 sm:p-4 rounded-2xl border-2 border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all text-left shadow-sm active:scale-[0.98]"
          >
            <div className="p-2.5 rounded-xl bg-gray-100 text-gray-600 mr-3 shrink-0">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xs sm:text-sm font-bold text-gray-900 transition-colors">
                Other Operations
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 leading-snug">
                Access full desktop web HRMS modules & features
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default IPhoneModeSelectionModal;
